import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api-client.js';
import { useAuth } from '../lib/auth-store.js';
import { ATLAS } from '../lib/foxhole-maps.js';
import { AuditLogModal } from '../components/AuditLogModal.js';

export interface AdminAccount {
  id: string;
  pseudo: string;
  email: string | null;
  discordId: string | null;
  avatarUrl: string | null;
  role: 'user' | 'editor' | 'admin';
  bannedAt: string | null;
  createdAt: string;
  lastSeenAt: string;
}

const REGIONS = [...ATLAS].sort((a, b) => a.mapName.localeCompare(b.mapName));

export function AdminPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const me = useAuth((s) => s.account);

  const [showCreate, setShowCreate] = useState(false);
  const [showRefresh, setShowRefresh] = useState(false);
  const [refreshMap, setRefreshMap] = useState('');
  const [refreshNote, setRefreshNote] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [createPseudo, setCreatePseudo] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [pwdEditId, setPwdEditId] = useState<string | null>(null);
  const [pwdEditValue, setPwdEditValue] = useState('');
  const [auditOpen, setAuditOpen] = useState<AdminAccount | null>(null);
  const [mutError, setMutError] = useState<string | null>(null);

  const accounts = useQuery({
    queryKey: ['admin', 'accounts'],
    queryFn: () => api.get<AdminAccount[]>('/api/admin/users?limit=100'),
  });

  const refreshMut = useMutation({
    mutationFn: (mapName: string | null) =>
      api.post<{ flushed: string[] | 'all' }>('/api/admin/foxhole/refresh', mapName ? { mapName } : {}),
    onSuccess: (data) => {
      setRefreshError(null);
      const flushed = data.flushed;
      setRefreshNote(`Flushed: ${flushed === 'all' ? 'all' : flushed.join(', ')}`);
      void queryClient.invalidateQueries({ queryKey: ['foxhole'] });
    },
    onError: (err: unknown) => {
      setRefreshNote(null);
      setRefreshError(err instanceof ApiError ? err.message : 'Refresh failed');
    },
  });

  const createMut = useMutation({
    mutationFn: () => api.post<AdminAccount>('/api/admin/users', { pseudo: createPseudo, password: createPassword }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'accounts'] });
      setShowCreate(false);
      setCreatePseudo('');
      setCreatePassword('');
      setCreateError(null);
    },
    onError: (e: unknown) => setCreateError(e instanceof ApiError ? e.message : 'Create failed'),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { role?: 'user' | 'editor' | 'admin'; banned?: boolean } }) =>
      api.patch<AdminAccount>(`/api/admin/users/${id}`, patch),
    onSuccess: () => {
      setMutError(null);
      return queryClient.invalidateQueries({ queryKey: ['admin', 'accounts'] });
    },
    onError: (e: unknown) => setMutError(e instanceof ApiError ? e.message : 'Operation failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/admin/users/${id}`),
    onSuccess: () => {
      setMutError(null);
      return queryClient.invalidateQueries({ queryKey: ['admin', 'accounts'] });
    },
    onError: (e: unknown) => setMutError(e instanceof ApiError ? e.message : 'Delete failed'),
  });

  const pwdMut = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api.patch<AdminAccount>(`/api/admin/users/${id}/password`, { password }),
    onSuccess: () => {
      setMutError(null);
      setPwdEditId(null);
      setPwdEditValue('');
      return queryClient.invalidateQueries({ queryKey: ['admin', 'accounts'] });
    },
    onError: (e: unknown) => setMutError(e instanceof ApiError ? e.message : 'Password update failed'),
  });

  if (!me || me.role !== 'admin') {
    return (
      <main className="min-h-screen bg-slate-950 p-6 text-slate-100">
        <p className="text-red-400">Forbidden — admin role required.</p>
        <Link to="/" className="text-sm text-emerald-400 hover:underline">
          ← Back
        </Link>
      </main>
    );
  }

  const data = accounts.data ?? [];
  const nDiscord = data.filter((a) => a.discordId !== null).length;
  const nLocal = data.filter((a) => a.discordId === null).length;
  const canCreate = createPseudo.trim().length > 0 && createPassword.length >= 12;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Header */}
      <header className="border-b border-slate-800 pb-4 px-6 pt-5">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-sm text-slate-400 hover:text-slate-200">
            ← Back
          </Link>
          <img src="/logo.png" alt="" className="h-8 w-8 object-contain" />
          <h1 className="text-2xl font-bold">
            Admin · Users
            {accounts.data && (
              <span className="ml-2 text-base font-normal text-slate-400">
                ({nDiscord} discord, {nLocal} local)
              </span>
            )}
          </h1>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => { setShowCreate((v) => !v); setCreateError(null); }}
            className="rounded bg-slate-700 px-3 py-1 text-sm hover:bg-slate-600"
          >
            {showCreate ? 'Cancel' : '＋ Create user'}
          </button>
          <button
            type="button"
            onClick={() => { setShowRefresh((v) => !v); setRefreshNote(null); setRefreshError(null); }}
            className="rounded bg-slate-700 px-3 py-1 text-sm hover:bg-slate-600"
          >
            {showRefresh ? 'Cancel' : '\u21BB WarAPI cache'}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-6 px-4 py-6">
        {/* WarAPI cache panel */}
        {showRefresh && (
          <div className="flex flex-wrap items-end gap-3 rounded border border-slate-700 bg-slate-900 p-4">
            <button
              type="button"
              disabled={refreshMut.isPending}
              onClick={() => refreshMut.mutate(null)}
              className="rounded bg-slate-700 px-3 py-1 text-sm hover:bg-slate-600 disabled:opacity-60"
            >
              {refreshMut.isPending ? 'Flushing…' : 'Flush all regions'}
            </button>
            <select
              value={refreshMap}
              onChange={(e) => setRefreshMap(e.target.value)}
              className="w-64 rounded bg-slate-800 px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-slate-500"
            >
              <option value="">— pick a region —</option>
              {REGIONS.map((r) => (
                <option key={r.key} value={r.mapName}>{r.mapName}</option>
              ))}
            </select>
            <button
              type="button"
              disabled={!refreshMap.trim() || refreshMut.isPending}
              onClick={() => refreshMut.mutate(refreshMap.trim())}
              className="rounded bg-slate-700 px-3 py-1 text-sm hover:bg-slate-600 disabled:opacity-40"
            >
              Flush region
            </button>
            {refreshNote && <p className="w-full text-xs text-emerald-400">{refreshNote}</p>}
            {refreshError && <p className="w-full text-xs text-red-400">{refreshError}</p>}
          </div>
        )}

        {/* Create user panel */}
        {showCreate && (
          <form
            className="flex flex-wrap items-end gap-3 rounded border border-slate-700 bg-slate-900 p-4"
            onSubmit={(e) => {
              e.preventDefault();
              createMut.mutate();
            }}
          >
            <label className="block">
              <span className="mb-1 block text-xs text-slate-400">Pseudo</span>
              <input
                value={createPseudo}
                onChange={(e) => setCreatePseudo(e.target.value)}
                required
                maxLength={40}
                placeholder="username"
                className="rounded bg-slate-800 px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-slate-500"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-slate-400">Password</span>
              <input
                type="password"
                value={createPassword}
                onChange={(e) => setCreatePassword(e.target.value)}
                required
                minLength={12}
                placeholder="••••••••"
                className="rounded bg-slate-800 px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-slate-500"
              />
            </label>
            <button
              type="submit"
              disabled={!canCreate || createMut.isPending}
              className="rounded bg-emerald-700 px-3 py-1.5 text-sm hover:bg-emerald-600 disabled:opacity-40"
            >
              {createMut.isPending ? 'Creating…' : 'Create'}
            </button>
            {createError && <p className="w-full text-xs text-red-400">{createError}</p>}
          </form>
        )}

        {/* Users table */}
        <section className="rounded border border-slate-800 bg-slate-900">
          {accounts.isLoading ? (
            <p className="p-4 text-sm text-slate-400">Loading…</p>
          ) : accounts.isError ? (
            <p className="p-4 text-sm text-red-400">{accounts.error instanceof Error ? accounts.error.message : 'Error'}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-slate-400">
                <tr>
                  <th className="px-2 py-2">Pseudo</th>
                  <th className="px-2 py-2">Type</th>
                  <th className="px-2 py-2">Role</th>
                  <th className="px-2 py-2">Banned</th>
                  <th className="px-2 py-2">Created</th>
                  <th className="px-2 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((a) => (
                  <tr key={a.id} className="odd:bg-slate-900/50 border-b border-slate-800/60 last:border-0 align-middle">
                    <td className="px-2 py-1 font-medium">
                      <button
                        type="button"
                        onClick={() => setAuditOpen(a)}
                        className="text-left hover:text-emerald-400 hover:underline"
                        title="Open audit log"
                      >
                        {a.pseudo}
                      </button>
                    </td>
                    <td className="px-2 py-1">
                      {a.discordId !== null ? (
                        <span className="rounded bg-indigo-800 px-2 py-0.5 text-xs">Discord</span>
                      ) : (
                        <span className="rounded bg-slate-700 px-2 py-0.5 text-xs">Local</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {a.role === 'admin' ? (
                        <span className="rounded bg-amber-700 px-2 py-0.5 text-xs uppercase">ADMIN</span>
                      ) : a.role === 'editor' ? (
                        <span className="rounded bg-emerald-700 px-2 py-0.5 text-xs uppercase">EDITOR</span>
                      ) : (
                        <span className="rounded bg-slate-700 px-2 py-0.5 text-xs uppercase">USER</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {a.bannedAt ? (
                        <span className="rounded bg-red-700 px-2 py-0.5 text-xs">banned</span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-slate-500">
                      {new Date(a.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-2 py-1">
                      {a.id === me.id ? null : pwdEditId === a.id ? (
                        /* Inline password editor */
                        <span className="flex items-center gap-1">
                          <input
                            type="password"
                            value={pwdEditValue}
                            onChange={(e) => setPwdEditValue(e.target.value)}
                            autoComplete="new-password"
                            placeholder="new password"
                            className="w-32 rounded bg-slate-800 px-2 py-0.5 text-xs outline-none focus:ring-1 focus:ring-slate-500"
                          />
                          <button
                            type="button"
                            disabled={pwdEditValue.length < 12 || pwdMut.isPending}
                            onClick={() => pwdMut.mutate({ id: a.id, password: pwdEditValue })}
                            className="rounded bg-emerald-700 px-2 py-0.5 text-xs hover:bg-emerald-600 disabled:opacity-40"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => { setPwdEditId(null); setPwdEditValue(''); }}
                            className="rounded bg-slate-700 px-2 py-0.5 text-xs hover:bg-slate-600"
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <span className="flex flex-wrap gap-1">
                          {a.role === 'user' ? (
                            <button
                              type="button"
                              onClick={() => patchMut.mutate({ id: a.id, patch: { role: 'editor' } })}
                              className="rounded bg-slate-700 px-2 py-0.5 text-xs hover:bg-slate-600"
                            >
                              Promote
                            </button>
                          ) : a.role === 'editor' ? (
                            <>
                              <button
                                type="button"
                                onClick={() => patchMut.mutate({ id: a.id, patch: { role: 'admin' } })}
                                className="rounded bg-slate-700 px-2 py-0.5 text-xs hover:bg-slate-600"
                              >
                                Promote
                              </button>
                              <button
                                type="button"
                                onClick={() => patchMut.mutate({ id: a.id, patch: { role: 'user' } })}
                                className="rounded bg-slate-700 px-2 py-0.5 text-xs hover:bg-slate-600"
                              >
                                Demote
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => patchMut.mutate({ id: a.id, patch: { role: 'editor' } })}
                              className="rounded bg-slate-700 px-2 py-0.5 text-xs hover:bg-slate-600"
                            >
                              Demote
                            </button>
                          )}
                          {a.bannedAt === null ? (
                            <button
                              type="button"
                              onClick={() => patchMut.mutate({ id: a.id, patch: { banned: true } })}
                              className="rounded bg-orange-700 px-2 py-0.5 text-xs hover:bg-orange-600"
                            >
                              Ban
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => patchMut.mutate({ id: a.id, patch: { banned: false } })}
                              className="rounded bg-emerald-700 px-2 py-0.5 text-xs hover:bg-emerald-600"
                            >
                              Unban
                            </button>
                          )}
                          {a.discordId === null && (
                            <button
                              type="button"
                              onClick={() => { setPwdEditId(a.id); setPwdEditValue(''); }}
                              className="rounded bg-slate-600 px-2 py-0.5 text-xs hover:bg-slate-500"
                            >
                              Chg pwd
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`Delete "${a.pseudo}"? This cannot be undone.`))
                                deleteMut.mutate(a.id);
                            }}
                            className="rounded bg-red-800 px-2 py-0.5 text-xs text-red-100 hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {mutError && (
            <p className="px-4 py-2 text-sm text-red-400">{mutError}</p>
          )}
        </section>
      </main>

      {auditOpen && (
        <AuditLogModal
          account={auditOpen}
          allAccounts={accounts.data ?? []}
          open={true}
          onClose={() => setAuditOpen(null)}
        />
      )}
    </div>
  );
}
